import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CustomDropdown from '../CustomDropdown';

// 30-minute time options (00:00–23:30); includes `current` if not on the grid
const generateTimeOptions = (current) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  if (current && !opts.includes(current)) opts.push(current);
  opts.sort();
  return opts.map((t) => ({ value: t, label: t }));
};

const CourtManagement = () => {
  const { adminUser } = useAdmin();
  const [courts, setCourts] = useState([]);
  const [courtTypes, setCourtTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    courtTypeId: '',
    active: true,
    costPerSlot: 0,
    order: 0,
    openingHours: {
      monday: { open: '09:00', close: '22:00' },
      tuesday: { open: '09:00', close: '22:00' },
      wednesday: { open: '09:00', close: '22:00' },
      thursday: { open: '09:00', close: '22:00' },
      friday: { open: '09:00', close: '22:00' },
      saturday: { open: '09:00', close: '22:00' },
      sunday: { open: '09:00', close: '22:00' },
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [courtsData, courtTypesData] = await Promise.all([
        adminApiService.getCourts(adminUser.idToken),
        adminApiService.getCourtTypes(adminUser.idToken)
      ]);
      setCourts(courtsData);
      setCourtTypes(courtTypesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (court) => {
    setEditingCourt(court);
    setFormData({
      name: court.name,
      courtTypeId: court.courtTypeId, // Use courtTypeId from the court data
      active: court.active,
      costPerSlot: court.costPerSlot,
      order: court.order ?? 0,
      openingHours: court.openingHours || formData.openingHours
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบสนามนี้?')) {
      return;
    }
    try {
      await adminApiService.deleteCourt(adminUser.idToken, id);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      courtTypeId: '',
      active: true,
      costPerSlot: 0,
      order: 0,
      openingHours: {
        monday: { open: '09:00', close: '22:00' },
        tuesday: { open: '09:00', close: '22:00' },
        wednesday: { open: '09:00', close: '22:00' },
        thursday: { open: '09:00', close: '22:00' },
        friday: { open: '09:00', close: '22:00' },
        saturday: { open: '09:00', close: '22:00' },
        sunday: { open: '09:00', close: '22:00' },
      }
    });
  };

  const openCreateModal = () => {
    setEditingCourt(null);
    resetForm();
    setShowModal(true);
  };

  const getCourtTypeName = (courtTypeId) => {
    const courtType = courtTypes.find(ct => ct.id === courtTypeId);
    return courtType ? courtType.name : 'ไม่ทราบ';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Send the court type ID to the API
      if (editingCourt) {
        await adminApiService.updateCourt(adminUser.idToken, editingCourt.id, formData);
      } else {
        await adminApiService.createCourt(adminUser.idToken, formData);
      }
      setShowModal(false);
      setEditingCourt(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const updateOpeningHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-900">สนาม</h2>
        <button
          onClick={openCreateModal}
          className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-base font-medium"
        >
          เพิ่มสนาม
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <ul className="divide-y divide-gray-200">
          {courts.map((court) => (
            <li key={court.id} className="px-4 sm:px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start sm:items-center">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">🏟️</span>
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <p className="text-base sm:text-sm font-medium text-gray-900">{court.name}</p>
                      <span className="text-sm text-gray-500">
                        ({getCourtTypeName(court.courtTypeId)})
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        ฿{court.costPerSlot}/ครึ่งชั่วโมง
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        ลำดับ {court.order ?? 0}
                      </span>
                    </div>
                    <div className="flex items-center mt-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          court.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {court.active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={() => handleEdit(court)}
                    className="flex-1 sm:flex-none text-blue-600 hover:text-blue-900 text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    แก้ไข
                  </button>
                  <button
                    onClick={() => handleDelete(court.id)}
                    className="flex-1 sm:flex-none text-red-600 hover:text-red-900 text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCourt ? 'แก้ไขสนาม' : 'เพิ่มสนาม'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อสนาม</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ประเภทสนาม</label>
                    <CustomDropdown
                      value={formData.courtTypeId}
                      onChange={(val) => setFormData({ ...formData, courtTypeId: val })}
                      placeholder="เลือกประเภทสนาม"
                      options={courtTypes.map((type) => ({ value: type.id, label: type.name }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ค่าใช้จ่ายต่อครึ่งชั่วโมง (บาท)</label>
                    <input
                      type="number"
                      value={formData.costPerSlot}
                      onChange={(e) => setFormData({ ...formData, costPerSlot: parseInt(e.target.value) })}
                      className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ลำดับการแสดงผล</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
                      className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      เลขน้อยแสดงก่อน (เช่น 1, 2, 3) — ถ้าเท่ากันจะเรียงตามชื่อสนาม
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                      เปิดใช้งาน
                    </label>
                  </div>
                </div>

                {/* Opening Hours */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">เวลาทำการ</h4>
                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(formData.openingHours).map(([day, hours]) => (
                      <div key={day} className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 w-20 capitalize">
                          {day === 'monday' ? 'จันทร์' : 
                           day === 'tuesday' ? 'อังคาร' :
                           day === 'wednesday' ? 'พุธ' :
                           day === 'thursday' ? 'พฤหัสบดี' :
                           day === 'friday' ? 'ศุกร์' :
                           day === 'saturday' ? 'เสาร์' :
                           day === 'sunday' ? 'อาทิตย์' : day}
                        </span>
                        <div className="flex items-center gap-2 flex-1">
                          <CustomDropdown
                            value={hours.open}
                            onChange={(val) => updateOpeningHours(day, 'open', val)}
                            options={generateTimeOptions(hours.open)}
                            placeholder="--:--"
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-500">ถึง</span>
                          <CustomDropdown
                            value={hours.close}
                            onChange={(val) => updateOpeningHours(day, 'close', val)}
                            options={generateTimeOptions(hours.close)}
                            placeholder="--:--"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCourt(null);
                      resetForm();
                    }}
                    className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingCourt ? 'อัปเดต' : 'สร้าง'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtManagement; 